import { CloseOutlined } from '@ant-design/icons'
import classnames from 'classnames/bind'
import React, { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import Button, { type ButtonVariant } from '../Button'
import styles from './Modal.module.css'

const cx = classnames.bind(styles)

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode | false
  cancelLabel?: string
  confirmLabel?: string
  onConfirm?: () => void
  confirmLoading?: boolean
  confirmDisabled?: boolean
  confirmVariant?: ButtonVariant
  width?: string | number
  className?: string
  bodyClassName?: string
  closeOnOverlayClick?: boolean
  showCloseButton?: boolean
  closeLabel?: string
}

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  cancelLabel,
  confirmLabel,
  onConfirm,
  confirmLoading = false,
  confirmDisabled = false,
  confirmVariant = 'primary',
  width = 'min(560px, calc(100vw - 32px))',
  className,
  bodyClassName,
  closeOnOverlayClick = true,
  showCloseButton = true,
  closeLabel = 'Close'
}) => {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return

    previousFocusRef.current = document.activeElement as HTMLElement | null
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    panelRef.current?.focus()

    return () => {
      document.body.style.overflow = originalOverflow
      document.removeEventListener('keydown', handleKeyDown)
      previousFocusRef.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  const renderFooter = (): React.ReactNode => {
    if (footer === false) return null
    if (footer !== undefined) return footer

    if (!cancelLabel && !confirmLabel) return null

    return (
      <div className={styles.footer}>
        {cancelLabel && (
          <Button variant="outline" onClick={onClose}>
            {cancelLabel}
          </Button>
        )}
        {confirmLabel && onConfirm && (
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            loading={confirmLoading}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        )}
      </div>
    )
  }

  const panelStyle: React.CSSProperties | undefined =
    width !== undefined ? { width } : undefined

  return createPortal(
    <div className={styles.root}>
      <div
        className={styles.overlay}
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={cx('panel', className)}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onClick={event => {
          event.stopPropagation()
        }}
        onMouseDown={event => {
          event.stopPropagation()
        }}
      >
        {(title || showCloseButton) && (
          <div className={styles.header}>
            {title && (
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label={closeLabel}
              >
                <CloseOutlined aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        <div className={cx('body', bodyClassName)}>{children}</div>
        {renderFooter()}
      </div>
    </div>,
    document.body
  )
}

export default Modal
